(() => {
  if (customElements.get('patas-frame-mockup')) return;
  const previews = new Map();
  const imageCache = new Map();
  const projectedCache = new Map();
  const frontalCache = new Map();
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const attribute = (element, name, value) => {
    if (value == null) { if (element.hasAttribute(name)) element.removeAttribute(name); }
    else if (element.getAttribute(name) !== String(value)) element.setAttribute(name, String(value));
  };
  function loadImage(src) {
    if (imageCache.has(src)) return imageCache.get(src);
    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => { imageCache.delete(src); reject(new Error('Mockup image unavailable')); };
      image.src = src;
    });
    imageCache.set(src, promise);
    if (imageCache.size > 12) imageCache.delete(imageCache.keys().next().value);
    return promise;
  }
  async function renderScene(scene, src) {
    if (scene.kind === 'frontal') return frontalBlob(scene, src);
    const canvas = scene.kind === 'tamanhos' ? await renderSizeComparison(scene, src) : await composeScene(scene, src);
    return canvasBlob(canvas);
  }
  function canvasBlob(canvas) {
    return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Mockup composition failed')), 'image/png'));
  }
  function frontalBlob(scene, src) {
    const key = JSON.stringify([scene, src]);
    if (frontalCache.has(key)) return frontalCache.get(key);
    const promise = composeScene(scene, src).then(canvasBlob).catch(error => { if (frontalCache.get(key) === promise) frontalCache.delete(key); throw error; });
    frontalCache.set(key, promise);
    if (frontalCache.size > 2) frontalCache.delete(frontalCache.keys().next().value);
    return promise;
  }
  async function composeScene(scene, src) {
    const [base, art, reflection, shadow] = await Promise.all([
      loadImage(scene.base), loadImage(src), scene.reflection ? loadImage(scene.reflection) : null,
      scene.shadow ? loadImage(scene.shadow) : null,
    ]);
    const resolution = 2;
    const canvas = document.createElement('canvas');
    canvas.width = scene.width * resolution; canvas.height = scene.height * resolution;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas unavailable');
    context.imageSmoothingQuality = 'high';
    context.drawImage(base, 0, 0, canvas.width, canvas.height);
    const q = scene.quad.map(([x, y]) => [x * resolution, y * resolution]);
    const geometry = globalThis.PatasMockupGeometry;
    // The art plane, inner shadow and acrylic stay the same between colors.
    // Color-specific clipping and wood occlusion are applied below.
    const projectedKey = JSON.stringify([src, scene.width, scene.height, scene.quad, scene.shadow, scene.reflection, scene.reflectionOpacity]);
    let projection = projectedCache.get(projectedKey);
    if (!projection) {
      const promise = (async () => {
        const paper = document.createElement('canvas');
        paper.width = 730 * resolution; paper.height = Math.round(paper.width * Math.SQRT2);
        const paperContext = paper.getContext('2d');
        if (!paperContext) throw new Error('Canvas unavailable');
        paperContext.imageSmoothingQuality = 'high';
        const scale = Math.max(paper.width / art.naturalWidth, paper.height / art.naturalHeight);
        const width = paper.width / scale, height = paper.height / scale;
        paperContext.drawImage(art, (art.naturalWidth - width) / 2, (art.naturalHeight - height) / 2, width, height, 0, 0, paper.width, paper.height);
        const bounds = { x: Math.floor(Math.min(...q.map(p => p[0]))), y: Math.floor(Math.min(...q.map(p => p[1]))) };
        const projected = document.createElement('canvas');
        projected.width = Math.ceil(Math.max(...q.map(p => p[0]))) - bounds.x;
        projected.height = Math.ceil(Math.max(...q.map(p => p[1]))) - bounds.y;
        const projectedContext = projected.getContext('2d');
        if (!projectedContext) throw new Error('Canvas unavailable');
        projectedContext.imageSmoothingQuality = 'high';
        if (Math.abs(q[0][1] - q[1][1]) < .01 && Math.abs(q[0][0] - q[3][0]) < .01) {
          projectedContext.drawImage(paper, q[0][0] - bounds.x, q[0][1] - bounds.y, q[1][0] - q[0][0], q[3][1] - q[0][1]);
        } else {
          const pixels = await geometry.rasterize(paperContext.getImageData(0, 0, paper.width, paper.height), q, { extendEdges: true });
          projectedContext.putImageData(new ImageData(pixels.data, pixels.width, pixels.height), 0, 0);
        }
        if (shadow) {
          projectedContext.globalCompositeOperation = 'multiply';
          projectedContext.drawImage(shadow, -bounds.x, -bounds.y, canvas.width, canvas.height);
        }
        if (reflection) {
          projectedContext.globalCompositeOperation = 'screen'; projectedContext.globalAlpha = scene.reflectionOpacity ?? 1;
          projectedContext.drawImage(reflection, -bounds.x, -bounds.y, canvas.width, canvas.height);
        }
        return { projected, bounds };
      })().catch(error => { if (projectedCache.get(projectedKey) === promise) projectedCache.delete(projectedKey); throw error; });
      projectedCache.set(projectedKey, promise); projection = promise;
      if (projectedCache.size > 6) projectedCache.delete(projectedCache.keys().next().value);
    }
    const { projected, bounds } = await projection;
    const inset = scene.kind === 'frontal' ? 0 : scene.type === 'tradicional' && scene.kind === 'apoiado' ? 1 : .5;
    const clip = geometry.inset(scene.clip || scene.quad, inset);
    context.save(); context.beginPath();
    clip.forEach(([x, y], i) => i ? context.lineTo(x * resolution, y * resolution) : context.moveTo(x * resolution, y * resolution));
    context.closePath(); context.clip();
    context.drawImage(projected, bounds.x, bounds.y);
    context.restore();
    if (scene.leftFrameOcclusion) {
      // The enlarged wood edge varies slightly along the aperture. Retain its
      // warm foreground pixels over the art while covering the neutral paper.
      const x = Math.max(0, Math.floor(Math.min(q[0][0], q[3][0])) - 4);
      const y = Math.max(0, Math.floor(q[0][1]) + 2);
      const width = Math.ceil(Math.max(q[0][0], q[3][0])) - x + 5;
      const height = Math.floor(q[3][1]) - y - 2;
      const frame = document.createElement('canvas'); frame.width = width; frame.height = height;
      const fc = frame.getContext('2d'); fc.imageSmoothingQuality = 'high';
      fc.drawImage(base, -x, -y, canvas.width, canvas.height);
      const reference = fc.getImageData(0, 0, width, height).data;
      const blended = context.getImageData(x, y, width, height);
      for (let row = 0; row < height; row++) {
        const left = q[0][0] + (y + row - q[0][1]) * (q[3][0] - q[0][0]) / (q[3][1] - q[0][1]);
        for (let col = Math.max(0, Math.floor(left - x) - 3); col < Math.min(width, Math.ceil(left - x) + 4); col++) {
          const i = (row * width + col) * 4;
          const darkFrame = scene.color === 'preta';
          const strength = darkFrame
            ? Math.max(0, Math.min(1, (170 - Math.max(reference[i], reference[i + 1], reference[i + 2])) / 60))
            : Math.max(0, Math.min(1, (reference[i] - reference[i + 2] - 20) / 15));
          if (!strength || (!darkFrame && reference[i] - reference[i + 1] < 5)) continue;
          for (let channel = 0; channel < 3; channel++) blended.data[i + channel] = Math.round(reference[i + channel] * strength + blended.data[i + channel] * (1 - strength));
        }
      }
      context.putImageData(blended, x, y);
    }
    return canvas;
  }
  async function renderSizeComparison(scene, src) {
    const [background, framed] = await Promise.all([
      loadImage(scene.base), renderScene(scene.referenceScene, src).then(blob => createImageBitmap(blob)),
    ]);
    try {
      const resolution = 2;
      const canvas = document.createElement('canvas');
      canvas.width = scene.width * resolution; canvas.height = scene.height * resolution;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas unavailable');
      context.imageSmoothingQuality = 'high';
      const backgroundScale = Math.max(canvas.width / background.naturalWidth, canvas.height / background.naturalHeight);
      const backgroundWidth = canvas.width / backgroundScale, backgroundHeight = canvas.height / backgroundScale;
      context.drawImage(background, (background.naturalWidth - backgroundWidth) / 2, (background.naturalHeight - backgroundHeight) / 2,
        backgroundWidth, backgroundHeight, 0, 0, canvas.width, canvas.height);
      context.scale(resolution, resolution);
      const layout = globalThis.PatasMockupGeometry.sizeLayout(scene);
      const q = scene.referenceScene.quad;
      const border = scene.type === 'filete' ? 15 : 45;
      const sx = [q[0][0] - border, q[0][0], q[1][0], q[1][0] + border].map(value => value * resolution);
      const sy = [q[0][1] - border, q[0][1], q[3][1], q[3][1] + border].map(value => value * resolution);
      const selectedIndex = layout.findIndex(paper => paper.name === scene.size);
      const smallerFrames = selectedIndex < 0 ? [] : layout.slice(0, selectedIndex).reverse();
      const selectedFrame = selectedIndex < 0 ? null : layout[selectedIndex];
      const largerFrames = selectedIndex < 0 ? [...layout].reverse() : layout.slice(selectedIndex + 1).reverse();
      // Smaller frames sit behind the selected artwork; larger frames keep
      // their existing foreground order.
      const paintFrame = paper => {
        const frame = document.createElement('canvas');
        frame.width = Math.ceil(paper.outerWidth * resolution); frame.height = Math.ceil(paper.outerHeight * resolution);
        const fc = frame.getContext('2d'); fc.imageSmoothingQuality = 'high'; fc.scale(resolution, resolution);
        const dx = [0, paper.face, paper.outerWidth - paper.face, paper.outerWidth];
        const dy = [0, paper.face, paper.outerHeight - paper.face, paper.outerHeight];
        // Nine slices retain the same physical face width for every paper size.
        for (let row = 0; row < 3; row++) for (let col = 0; col < 3; col++) {
          if (paper.name !== scene.size && row === 1 && col === 1) continue;
          fc.drawImage(framed, sx[col], sy[row], sx[col + 1] - sx[col], sy[row + 1] - sy[row],
            dx[col], dy[row], dx[col + 1] - dx[col], dy[row + 1] - dy[row]);
        }
        context.save(); context.globalAlpha = paper.opacity; context.shadowColor = '#32291c55';
        context.shadowBlur = 10 * resolution; context.shadowOffsetX = 3 * resolution; context.shadowOffsetY = 3 * resolution;
        context.drawImage(frame, paper.x, paper.y, paper.outerWidth, paper.outerHeight);
        context.restore();
      };
      const drawSizeLabel = paper => {
        const label = paper.name;
        const paddingX = 9, labelHeight = 30;
        context.font = '600 20px system-ui, sans-serif';
        context.textAlign = 'left'; context.textBaseline = 'middle';
        const labelWidth = context.measureText(label).width + paddingX * 2;
        const x = paper.x + paper.face + 8;
        const y = paper.y + paper.face + 8;
        context.save(); context.globalAlpha = 1;
        context.fillStyle = '#fcf9f5';
        context.strokeStyle = '#49392d'; context.lineWidth = 1;
        context.beginPath();
        if (typeof context.roundRect === 'function') context.roundRect(x, y, labelWidth, labelHeight, 7);
        else context.rect(x, y, labelWidth, labelHeight);
        context.fill(); context.stroke();
        context.fillStyle = '#49392d';
        context.fillText(label, x + paddingX, y + labelHeight / 2);
        context.restore();
      };
      for (const paper of smallerFrames) paintFrame(paper);
      // The selected frame is painted after smaller labels, so its artwork
      // covers them along with the smaller frame contours.
      for (const paper of smallerFrames) drawSizeLabel(paper);
      if (selectedFrame) paintFrame(selectedFrame);
      for (const paper of largerFrames) paintFrame(paper);
      for (const paper of largerFrames) drawSizeLabel(paper);
      return canvas;
    } finally { framed.close(); }
  }
  globalThis.PatasMockupRenderer ??= { renderScene };

  class PatasFrameMockup extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('media-gallery');
      this.section = this.closest('.shopify-section') || document;
      try { this.config = JSON.parse(this.querySelector('[data-mockup-config]')?.textContent || 'null'); }
      catch { return; }
      if (!this.config || !this.gallery) return;
      this.variantId = String(this.config.variantId);
      this.inputKey = null; this.source = null;
      this.version = 0; this.cache = new Map(); this.urls = new Set();
      this.prefetchedBases = new Set(); this.prefetchController = new AbortController();
      this.onChange = () => {
        clearTimeout(this.variantTimer);
        this.variantTimer = setTimeout(() => this.syncArtwork(), 50);
      };
      this.section.addEventListener('change', this.onChange, true);
      this.galleryIndex = null;
      this.onGalleryClick = event => {
        const button = event.target.closest('button[data-patas-mockup-index]');
        if (!button) return;
        this.galleryIndex = Number(button.dataset.patasMockupIndex);
        this.updateSizeNote(this.sceneList());
      };
      this.gallery.addEventListener('click', this.onGalleryClick, true);
      this.observer = new MutationObserver(records => {
        if (records.some(record => record.attributeName === 'aria-hidden' && record.target.matches('slideshow-slide[data-patas-mockup-index]'))) {
          const active = this.gallery.querySelector('slideshow-slide[data-patas-mockup-index][aria-hidden="false"]');
          if (active) this.galleryIndex = Number(active.dataset.patasMockupIndex);
        }
        this.syncArtwork();
      });
      this.observer.observe(this.gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'alt', 'hidden', 'aria-hidden'] });
      this.syncArtwork();
    }
    disconnectedCallback() {
      this.version++;
      this.observer?.disconnect();
      this.section?.removeEventListener('change', this.onChange, true);
      this.gallery?.removeEventListener('click', this.onGalleryClick, true);
      clearTimeout(this.variantTimer); this.clearCache();
      clearTimeout(this.prefetchTimer); this.prefetchController?.abort();
    }
    clearCache() {
      for (const url of this.urls || []) URL.revokeObjectURL(url);
      this.urls?.clear(); this.cache?.clear();
    }
    pruneCache(scenes, src) {
      const active = new Set(scenes.map(scene => scene.slug + '|' + src));
      for (const [key, promise] of this.cache) {
        if (this.cache.size <= 8) break;
        if (active.has(key)) continue;
        this.cache.delete(key);
        promise.then(url => { this.urls.delete(url); URL.revokeObjectURL(url); }).catch(() => {});
      }
    }
    prefetchBases(scenes) {
      if (typeof fetch !== 'function' || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      for (const scene of scenes) this.prefetchedBases.add(scene.base);
      clearTimeout(this.prefetchTimer);
      this.prefetchTimer = setTimeout(() => {
        if (!this.isConnected) return;
        const urls = this.config.scenes.filter(scene => scene.type === scenes[0]?.type).map(scene => scene.base)
          .filter(url => !this.prefetchedBases.has(url));
        for (const url of urls) this.prefetchedBases.add(url);
        // Warm the HTTP cache at low priority without decoding more full-size images.
        Promise.allSettled(urls.map(url => fetch(url, { cache: 'force-cache', priority: 'low', signal: this.prefetchController.signal })
          .then(response => response.ok ? response.arrayBuffer() : null)));
      }, 300);
    }
    sceneList() {
      const selections = [...this.section.querySelectorAll('[data-patas-frame-variants] [data-template-option]')].map(input => input.value);
      const selected = selections.length ? this.config.variants.find(v => v.options.every((option, i) => option === selections[i])) : null;
      const id = selected?.id || this.section.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
      if (this.config.variants.some(v => String(v.id) === String(id))) this.variantId = String(id);
      const variant = this.config.variants.find(v => String(v.id) === this.variantId) || this.config.variants[0];
      const options = (selections.length ? selections : variant?.options || []).map(normalize);
      const type = options.includes('filete') ? 'filete' : 'tradicional';
      const color = ['branca', 'natural', 'marrom', 'preta'].find(c => options.includes(c)) || 'natural';
      const scenes = ['frontal', 'angulo', 'apoiado'].map(kind =>
        this.config.scenes.find(s => s.kind === kind && s.type === type && s.color === color)
      ).filter(Boolean);
      if (this.config.sizeComparison && scenes[0]?.kind === 'frontal') {
        const size = (options.find(option => /^a[1-5]$/.test(option)) || 'a3').toUpperCase();
        const availableSizes = [...new Set(this.config.variants.filter(v => {
          const values = v.options.map(normalize); return values.includes(type) && values.includes(color);
        }).map(v => v.options.map(normalize).find(value => /^a[1-5]$/.test(value))?.toUpperCase()).filter(Boolean))];
        scenes.push({ ...this.config.sizeComparison, kind: 'tamanhos', type, color, size,
          availableSizes, referenceScene: scenes[0],
          unavailableLabel: this.config.labels.sizeUnavailable,
          slug: `tamanhos-${type}-${color}-${size}` });
      }
      return scenes;
    }
    slots(index) { return [...this.gallery.querySelectorAll('[data-patas-mockup-index="' + index + '"]')]; }
    showCount(count) {
      for (const slot of this.gallery.querySelectorAll('[data-patas-mockup-index]')) slot.hidden = Number(slot.dataset.patasMockupIndex) >= count;
      for (const total of this.gallery.querySelectorAll('[data-patas-mockup-total]')) if (total.textContent !== String(count)) total.textContent = String(count);
    }
    firstSlide() { this.gallery.slideshow?.select(0, undefined, { animate: false }); }
    updateSizeNote(scenes) {
      const note = this.gallery.querySelector('.patas-mockup-size-note');
      if (!note || this.dataset.status === 'pending') return;
      const comparison = scenes.find(scene => scene.kind === 'tamanhos');
      const visible = this.galleryIndex === 3 || (this.galleryIndex == null && this.gallery.querySelector('slideshow-slide[data-patas-mockup-index="3"][aria-hidden="false"]'));
      attribute(note, 'hidden', comparison && visible && this.gallery.hasAttribute('data-mockups-active') ? '' : null);
    }
    paint(index, src, alt, composed, scene) {
      for (const slot of this.slots(index)) {
        for (const image of slot.querySelectorAll('img:not([data-patas-template-overlay])')) {
          attribute(image, 'srcset', null); attribute(image, 'sizes', null);
          attribute(image, 'src', src); attribute(image, 'alt', alt || '');
          // Identify the composed result, not the hidden personalization overlay.
          attribute(image, 'data-patas-preview-for', composed && this.dataset.view === 'result' ? this.source : null);
          image.style.objectFit = composed && scene?.kind === 'tamanhos' ? 'contain' : '';
          if (image.classList.contains('product-media__image')) attribute(image, 'data_max_resolution', src);
        }
        const ratio = composed ? `${scene?.width || 1122} / ${scene?.height || 1402}` : String(this.config.originalMedia[index]?.ratio || 1);
        for (const media of slot.querySelectorAll('.product-media')) media.style.setProperty('--ratio', ratio);
        slot.style.setProperty('--media-preview-ratio', ratio);
        if (slot.matches('button')) slot.style.setProperty('--aspect-ratio', ratio);
      }
    }
    restoreOriginals(photo) {
      const count = photo ? 1 : Math.max(1, this.config.originalMedia.length);
      this.gallery.removeAttribute('data-mockups-active'); this.showCount(count);
      const note = this.gallery.querySelector('.patas-mockup-size-note');
      if (note) note.hidden = false;
      for (let i = 0; i < count; i++) {
        const media = photo || this.config.originalMedia[i] || { src: this.config.artwork, alt: this.config.title };
        this.paint(i, media.src, media.alt, false);
      }
      this.firstSlide();
    }
    blobUrl(scene, src) {
      const key = scene.slug + '|' + src;
      if (this.cache.has(key)) return this.cache.get(key);
      const promise = globalThis.PatasMockupRenderer.renderScene(scene, src).then(blob => {
        const url = URL.createObjectURL(blob);
        if (this.cache.get(key) !== promise) { URL.revokeObjectURL(url); throw new Error('Stale mockup'); }
        this.urls.add(url); return url;
      }).catch(error => { if (this.cache.get(key) === promise) this.cache.delete(key); throw error; });
      this.cache.set(key, promise); return promise;
    }
    syncArtwork() {
      if (!this.config || !this.isConnected) return;
      const scenes = this.sceneList(), productId = this.config.productId;
      let preview = this.gallery.querySelector('[data-patas-template-overlay]:not([hidden])');
      const loading = Boolean(this.gallery.querySelector('[data-patas-gallery-loading]'));
      const personalized = Boolean(this.section.querySelector('[data-patas-custom]')) || this.section.querySelector('[data-template-mode][value="personalizada"]')?.checked;
      if (loading) previews.delete(productId);
      else if (preview) previews.set(productId, { src: preview.getAttribute('src'), alt: preview.alt });
      else if (personalized && previews.has(productId)) {
        const remembered = previews.get(productId);
        preview = { alt: remembered.alt, getAttribute: () => remembered.src };
      } else if (!personalized) previews.delete(productId);
      const photo = preview?.alt === 'Foto do seu pet';
      const src = loading ? null : preview?.getAttribute('src') || (this.config.requiresPreview ? null : this.config.artwork);
      const view = photo ? 'photo' : preview ? 'result' : 'original';
      const key = JSON.stringify([src, view, this.variantId, scenes.map(scene => scene.slug), loading]);
      if (key === this.inputKey) { this.updateSizeNote(scenes); return; }
      this.inputKey = key; this.dataset.view = view;
      const version = ++this.version;
      if (src !== this.source) { this.source = src; this.clearCache(); }
      if (!src || photo) {
        this.dataset.status = 'original';
        this.restoreOriginals(photo ? { src, alt: preview.alt } : null); return;
      }
      this.dataset.status = 'pending';
      const keepComparison = this.galleryIndex === 3 || (this.galleryIndex == null && Boolean(this.gallery.querySelector('slideshow-slide[data-patas-mockup-index="3"][aria-hidden="false"]')));
      Promise.all(scenes.map((scene, index) => this.blobUrl(scene, src).then(url => {
        if (version !== this.version || !this.isConnected) return;
        const label = [this.config.labels[scene.kind], scene.size, this.config.labels[scene.type], this.config.labels[scene.color]].filter(Boolean).join(' · ');
        this.paint(index, url, (preview?.alt || this.config.title) + (label ? ' · ' + label : ''), true, scene);
        if (index === (keepComparison ? 3 : 0)) {
          this.gallery.setAttribute('data-mockups-active', ''); this.showCount(scenes.length);
          if (keepComparison) this.gallery.slideshow?.select(3, undefined, { animate: false });
          else this.firstSlide();
        }
      }))).then(() => {
        if (version !== this.version || !this.isConnected) return;
        this.dataset.status = 'ready';
        this.updateSizeNote(scenes);
        this.pruneCache(scenes, src);
        this.prefetchBases(scenes);
      }).catch(() => {
        if (version !== this.version || !this.isConnected) return;
        this.version++;
        this.dataset.status = 'failed'; this.restoreOriginals();
      });
    }
  }
  customElements.define('patas-frame-mockup', PatasFrameMockup);
})();
